import axios from 'axios'
import { reverse } from 'named-urls'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Toasts, useModalContext } from '@fourthwall/components'
import { getDefaultErrorMessage } from '@fourthwall/utils/dist/queryClient'
import { routing } from '@utils/routing'

import { useOrderIdParam } from '../../hooks/useOrderIdParam'
import { useOrderGroupedByShipmentsQuery } from '../../queries/useOrderGroupedByShipmentsQuery'
import { useOrderRefundQuery } from '../../queries/useOrderRefundQuery'
import OrderRefundView from './OrderRefundView'
import type { ExpectedCostSplit } from './modules/OrderRefundConfirmModal'
import { OrderRefundConfirmModal } from './modules/OrderRefundConfirmModal'
import { useRefundOrderMutation } from './queries/useRefundOrderMutation'

export const OrderRefundContainer = () => {
  const { orderId } = useOrderIdParam()
  const navigate = useNavigate()
  const { open } = useModalContext()
  const { orderGroupedByShipmentsQuery } = useOrderGroupedByShipmentsQuery([{ orderId }])
  const { orderRefundQuery, invalidateOrderRefundQuery } = useOrderRefundQuery([orderId])
  const { refundOrderMutation } = useRefundOrderMutation({ onError: undefined })

  const refundOrder = (amount: string, expectedCostSplit: ExpectedCostSplit, reason?: string) => {
    refundOrderMutation.mutate(
      [
        orderId,
        {
          amount: { value: parseFloat(amount), currency: 'USD' },
          reason,
          expectedCostSplit,
          metadata: {},
        },
      ],
      {
        onSuccess: () => {
          invalidateOrderRefundQuery()
          navigate(reverse(routing.contributions.orders.order.self, { orderId }))
          Toasts.notify('Order has been refunded successfully.', { type: 'info' })
        },
        onError: (error: unknown) => {
          if (
            axios.isAxiosError(error) &&
            error.response?.data.code === 'ORDER_REFUND_EXPECTED_CHARGE_AMOUNT_CHANGED'
          ) {
            open(OrderRefundConfirmModal, {
              orderId,
              initialReason: reason,
              error: 'Your balance has changed and cannot cover refund amount. Please try again.',
              amount,
              onConfirm: (expectedCostSplit, reason) => {
                refundOrder(amount, expectedCostSplit, reason)
              },
            })

            return
          }
          Toasts.notify(getDefaultErrorMessage(error), { type: 'error' })
        },
      },
    )
  }
  const handleOrderRefund = (amount: string) => {
    open(OrderRefundConfirmModal, {
      orderId,
      amount,
      onConfirm: (expectedCostSplit, reason) => {
        refundOrder(amount, expectedCostSplit, reason)
      },
    })
  }

  return (
    <OrderRefundView
      orderId={orderId}
      friendlyId={orderGroupedByShipmentsQuery.data?.friendlyId}
      order={orderGroupedByShipmentsQuery.data}
      donation={orderGroupedByShipmentsQuery.data?.donation || undefined}
      isLoading={
        orderGroupedByShipmentsQuery.isLoading ||
        orderRefundQuery.isLoading ||
        refundOrderMutation.isLoading
      }
      remainingRefundAmount={orderRefundQuery.data?.remainingRefundAmount.value}
      onOrderRefund={handleOrderRefund}
    />
  )
}
